import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { X, ChevronDown, ChevronUp, Heart, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSessionValidation } from "@/hooks/useSessionValidation";

interface ProfileFormData {
  dateOfBirth: string;
  gender: string;
  phoneNumber: string;
  termsAccepted: boolean;
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactRelationship: string;
}

interface ProfileCompletionPromptProps {
  onComplete: () => void;
  onCancel: () => void;
}

// Helper function to format UK phone numbers
const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return phoneNumber;
  
  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // If it starts with 07 and is 11 digits, convert to +44
  if (digitsOnly.startsWith('07') && digitsOnly.length === 11) {
    return '+44' + digitsOnly.substring(1);
  }
  
  // If it already starts with +44, keep as is
  if (phoneNumber.startsWith('+44')) {
    return phoneNumber;
  }
  
  // Return original if it doesn't match UK mobile pattern
  return phoneNumber;
};

const ProfileCompletionPrompt = ({ onComplete, onCancel }: ProfileCompletionPromptProps) => {
  const { user, session, refreshSession, sessionError } = useAuth();
  const { validateSession, hasValidSession } = useSessionValidation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [emergencyContactOpen, setEmergencyContactOpen] = useState(false);
  const [sessionValidated, setSessionValidated] = useState(false);

  const form = useForm<ProfileFormData>({
    defaultValues: {
      dateOfBirth: "",
      gender: "",
      phoneNumber: "",
      termsAccepted: false,
      emergencyContactName: "",
      emergencyContactPhone: "",
      emergencyContactRelationship: "",
    },
  });

  // Session validation on mount
  useEffect(() => {
    const performInitialValidation = async () => {
      console.log('ProfileCompletionPrompt: Performing initial session validation');
      console.log('User:', !!user);
      console.log('Session:', !!session);
      console.log('Session error:', sessionError);
      
      if (!user || !session) {
        console.log('ProfileCompletionPrompt: Missing user or session, waiting...');
        return;
      }
      
      const isValid = await validateSession();
      setSessionValidated(isValid);
      
      if (!isValid) {
        console.log('ProfileCompletionPrompt: Session validation failed');
        setError("Authentication session is invalid. Please sign in again.");
        return;
      }
      
      console.log('ProfileCompletionPrompt: Session validated successfully');
    };
    
    if (user && session && !sessionValidated) {
      performInitialValidation();
    }
  }, [user, session, validateSession, sessionValidated, sessionError]);

  // Fetch existing profile data when session is validated
  useEffect(() => {
    const fetchExistingProfile = async () => {
      if (!sessionValidated || !user) return;

      try {
        console.log('ProfileCompletionPrompt: Fetching existing profile data');
        
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('date_of_birth, gender, phone_number, terms_accepted_at, emergency_contact_name, emergency_contact_phone, emergency_contact_relationship')
          .eq('id', user.id)
          .single();

        if (!error && profile) {
          console.log('ProfileCompletionPrompt: Profile data found');
          
          // Check if terms have already been accepted
          const termsAlreadyAccepted = !!profile.terms_accepted_at;
          setHasAcceptedTerms(termsAlreadyAccepted);

          // Check if emergency contact data exists
          const hasEmergencyContact = !!(profile.emergency_contact_name || profile.emergency_contact_phone || profile.emergency_contact_relationship);
          if (hasEmergencyContact) {
            setEmergencyContactOpen(true);
          }

          // Update form with existing data
          form.reset({
            dateOfBirth: profile.date_of_birth || "",
            gender: profile.gender || "",
            phoneNumber: profile.phone_number || "",
            termsAccepted: termsAlreadyAccepted, // Auto-accept if already accepted
            emergencyContactName: profile.emergency_contact_name || "",
            emergencyContactPhone: profile.emergency_contact_phone || "",
            emergencyContactRelationship: profile.emergency_contact_relationship || "",
          });
        }
      } catch (error) {
        console.error('ProfileCompletionPrompt: Error fetching existing profile:', error);
        setError("Failed to load profile data. Please try again.");
      } finally {
        setInitialDataLoaded(true);
      }
    };

    fetchExistingProfile();
  }, [user, form, sessionValidated]);

  const onSubmit = async (data: ProfileFormData) => {
    // Validate session before proceeding
    const isValid = await validateSession();
    if (!isValid) {
      setError("Your session has expired. Please sign in again to continue.");
      return;
    }

    // If terms haven't been accepted before and they're not accepting now, show error
    if (!hasAcceptedTerms && !data.termsAccepted) {
      setError("You must accept the terms and conditions to continue.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      console.log('ProfileCompletionPrompt: Starting profile update');
      
      const { error: updateError } = await supabase.auth.updateUser({
        data: {
          date_of_birth: data.dateOfBirth,
          gender: data.gender,
        }
      });

      if (updateError) {
        console.error('ProfileCompletionPrompt: Auth update error:', updateError);
        setError(updateError.message);
        return;
      }

      // Update the profiles table with all the data including phone number (if provided)
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser) {
        const updateData: any = {
          date_of_birth: data.dateOfBirth,
          gender: data.gender,
        };

        // Only include phone_number if it's provided
        if (data.phoneNumber && data.phoneNumber.trim()) {
          // Format phone number to include +44 for storage
          const formattedPhoneNumber = data.phoneNumber.startsWith('0') 
            ? `+44${data.phoneNumber.slice(1)}` 
            : `+44${data.phoneNumber}`;
          updateData.phone_number = formattedPhoneNumber;
        }

        // Include emergency contact information if provided
        if (data.emergencyContactName && data.emergencyContactName.trim()) {
          updateData.emergency_contact_name = data.emergencyContactName.trim();
        }
        if (data.emergencyContactPhone && data.emergencyContactPhone.trim()) {
          updateData.emergency_contact_phone = formatPhoneNumber(data.emergencyContactPhone.trim());
        }
        if (data.emergencyContactRelationship && data.emergencyContactRelationship.trim()) {
          updateData.emergency_contact_relationship = data.emergencyContactRelationship;
        }

        // Only update terms_accepted_at if they haven't been accepted before
        if (!hasAcceptedTerms) {
          updateData.terms_accepted_at = new Date().toISOString();
        }

        const { error: profileError } = await supabase
          .from('profiles')
          .update(updateData)
          .eq('id', currentUser.id);

        if (profileError) {
          console.error('ProfileCompletionPrompt: Profile update error:', profileError);
          setError("Failed to update profile. Please try again.");
          return;
        }
      }

      console.log('ProfileCompletionPrompt: Profile update completed successfully');
      onComplete();
    } catch (err) {
      console.error("ProfileCompletionPrompt: Profile completion error:", err);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Show loading while validating session or loading initial data
  if (!sessionValidated || !initialDataLoaded) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <Card className="w-full max-w-md">
          <CardContent className="p-6">
            <div className="text-center space-y-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <div>
                <p className="text-sm text-gray-600">Validating your session...</p>
                {sessionError && (
                  <p className="text-xs text-red-600 mt-1">Session issue detected</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show session error if validation failed
  if (!hasValidSession) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <Card className="w-full max-w-md">
          <CardContent className="p-6">
            <div className="text-center space-y-4">
              <AlertTriangle className="h-12 w-12 text-red-500 mx-auto" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Authentication Required</h3>
                <p className="text-sm text-gray-600 mt-2">
                  Your session has expired or is missing. Please sign in again to complete your profile.
                </p>
                {sessionError && (
                  <p className="text-xs text-red-600 mt-2">Error: {sessionError}</p>
                )}
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={onCancel}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => window.location.href = '/auth'}
                  className="flex-1"
                >
                  Sign In
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-md relative max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
          disabled={loading}
        >
          <X className="h-5 w-5" />
        </button>

        <CardHeader>
          <CardTitle className="text-xl font-semibold text-center">
            Complete Your Profile
          </CardTitle>
          <p className="text-gray-600 text-sm text-center">
            Please provide the following information to continue with your booking
          </p>
        </CardHeader>
        
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="dateOfBirth"
                rules={{ required: "Date of birth is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date of Birth</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="date"
                        className="w-full"
                        disabled={loading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="gender"
                rules={{ required: "Gender is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Gender</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      value={field.value}
                      disabled={loading}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select your gender" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                        <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phoneNumber"
                rules={{ 
                  pattern: {
                    value: /^(07\d{9}|\d{10})?$/,
                    message: "Please enter a valid UK mobile number (e.g., 07123456789) or leave empty"
                  }
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number (Optional)</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="tel"
                        placeholder="e.g., 07123456789 (optional)"
                        className="w-full"
                        disabled={loading}
                      />
                    </FormControl>
                    <p className="text-xs text-gray-500 mt-1">
                      For booking reminders and updates (SMS notifications are enabled by default)
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Emergency Contact Section */}
              <Collapsible open={emergencyContactOpen} onOpenChange={setEmergencyContactOpen}>
                <CollapsibleTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-between text-left h-auto p-3"
                    disabled={loading}
                  >
                    <div className="flex items-center gap-2">
                      <Heart className="h-4 w-4 text-red-500" />
                      <span>Emergency Contact (Optional)</span>
                    </div>
                    {emergencyContactOpen ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 mt-4">
                  <div className="text-sm text-gray-600 mb-3">
                    Provide emergency contact information for safety purposes during your sessions.
                  </div>

                  <FormField
                    control={form.control}
                    name="emergencyContactName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Emergency Contact Name</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Full name of emergency contact"
                            className="w-full"
                            disabled={loading}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="emergencyContactPhone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Emergency Contact Phone</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="tel"
                            placeholder="e.g., 07123456789"
                            className="w-full"
                            disabled={loading}
                          />
                        </FormControl>
                        <p className="text-xs text-gray-500 mt-1">
                          UK mobile numbers starting with 07 will be automatically formatted to +44
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="emergencyContactRelationship"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Relationship</FormLabel>
                        <Select 
                          onValueChange={field.onChange} 
                          value={field.value}
                          disabled={loading}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select relationship" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="parent">Parent</SelectItem>
                            <SelectItem value="spouse">Spouse</SelectItem>
                            <SelectItem value="partner">Partner</SelectItem>
                            <SelectItem value="sibling">Sibling</SelectItem>
                            <SelectItem value="child">Child</SelectItem>
                            <SelectItem value="friend">Friend</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CollapsibleContent>
              </Collapsible>

              {!hasAcceptedTerms && (
                <FormField
                  control={form.control}
                  name="termsAccepted"
                  rules={{ required: "You must accept the terms and conditions" }}
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={loading}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="text-sm">
                          I agree to the{" "}
                          <Link 
                            to="/terms" 
                            className="text-blue-600 hover:text-blue-700 underline"
                            target="_blank"
                          >
                            Terms of Service
                          </Link>
                          {" "}and{" "}
                          <Link 
                            to="/privacy" 
                            className="text-blue-600 hover:text-blue-700 underline"
                            target="_blank"
                          >
                            Privacy Policy
                          </Link>
                        </FormLabel>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={loading}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1"
                >
                  {loading ? "Saving..." : "Complete Profile"}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileCompletionPrompt;
